import Location from "../../../hooks/Location";
import { useEffect, useRef, useState } from "react";
import { client } from "../../../hooks/Client";
import { bucket } from "../../../firebase/config";
import { v4 as uuid } from "uuid";
import IntakeFormSingleChoice from "./IntakeFormSingleChoice.jsx";
import IntakeFormCheckboxOptions from "./IntakeFormCheckboxOptions.jsx";
import { buildLocationFromPdokDoc, PDOK_FL } from "../../../helpers/pdokLocation";
import spinner from "../../../images/spinner-ripple.svg";
import fetchPublicIntakeForm from "../../../helpers/intakeForms/fetchPublicIntakeForm";
import buildIntakeSubmission, { HONEYPOT_KEY } from "../../../helpers/intakeForms/buildIntakeSubmission";
import submitIntakeForm from "../../../helpers/intakeForms/submitIntakeForm";
import isCheckboxChecked from "../../../helpers/intakeForms/isCheckboxChecked";
import isAddressLookupField from "../../../helpers/intakeForms/isAddressLookupField";
import { addressMismatchMessage } from "../../../helpers/pdok/verifyAddressMatch";
import checkAddress from "../../../helpers/pdok/checkAddress";
import IntakeFormMatrixField from "./IntakeFormMatrixField.jsx";
import getColumnType from "../../../helpers/intakeForms/getColumnType";
import isValidCellValue from "../../../helpers/intakeForms/isValidCellValue";
import isMatrixComplete from "../../../helpers/intakeForms/isMatrixComplete";
import usePermissions from "../../../hooks/usePermissions";

/**
 * Het publieke intakeformulier: de pagina die een bewoner via een gedeelde link
 * invult, zonder login.
 *
 * Deze pagina leest en schrijft NIETS rechtstreeks in Firestore. Dat mocht ooit
 * wel, en dat ging mis: de regels op `IntakeForms` zijn team-only, en een
 * geweigerde read levert geen fout op maar een lege lijst. Captcha en het
 * privacy-vinkje vielen daardoor stil terug op "uit", en het aanmaken van het
 * huishouden werd geweigerd — zonder dat de bewoner iets zag. Alles loopt nu via
 * twee Cloud Functions, en zolang het formulier niet geladen is, wordt er niets
 * getoond en niets verstuurd.
 *
 * Eén uitzondering: geüploade bestanden gaan nog rechtstreeks naar Storage. Dat
 * werkt vandaag omdat de Storage-regels wagenwijd openstaan — een apart en
 * ernstiger probleem, gelogd als inbox #122. Zodra die regels dichtgaan, moet
 * ook deze upload via de server lopen.
 * AI-CONTEXT: See docs/decisions.md#adr-022
 * AI-CONTEXT: See docs/pitfalls.md#security-public-page-reads-its-own-gates
 */
const IntakeFormOpen = () => {
  // State
  const [responses, setResponses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  /** @type {ReturnType<typeof useState<string>>} */
  const [loadError, setLoadError] = useState("");
  /** @type {ReturnType<typeof useState<string>>} */
  const [submitError, setSubmitError] = useState("");

  // Form meta from Firestore
  // Waar de antwoorden landen (huishouden of deelnemer) bepaalt de Cloud
  // Function zelf; hier is alleen nodig of dit een intake- of standaardformulier
  // is, om te controleren dat er een huishouden aan hangt.
  const [formType, setFormType] = useState("intake"); // "intake" | "standard"
  const [formSelected, setFormSelected] = useState(null); // ID of selected persona/adress for standard form
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [captchaChecked, setCaptchaChecked] = useState(false);
  // Het lokveld. Staat buiten beeld, heeft geen label en is niet met de
  // tab-toets te bereiken, dus een bezoeker komt er nooit aan. Een script dat
  // blind elk invoerveld vult wel — en dan weigert de Cloud Function de
  // inzending vóór hij iets kost.
  //
  // Waarom dit er is: het vinkje 'Ik ben geen robot' hieronder is een gewone
  // checkbox zonder controle erachter; de waarde ervan verlaat de browser niet
  // eens. Dit is de eerste echte drempel op dit formulier.
  // AI-CONTEXT: See docs/inbox.md #142
  const [honeypot, setHoneypot] = useState('');
  const [privacyCheckboxEnabled, setPrivacyCheckboxEnabled] = useState(false);
  const [privacyTextBefore, setPrivacyTextBefore] = useState("");
  const [privacyLinkLabel, setPrivacyLinkLabel] = useState("");
  const [privacyLinkUrl, setPrivacyLinkUrl] = useState("");
  const [privacyTextAfter, setPrivacyTextAfter] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [thankYouText, setThankYouText] = useState("");
  const [uploadFiles, setUploadFiles] = useState({}); // fieldId -> File
  const [matrixMeta, setMatrixMeta] = useState({}); // fieldId -> { rows, columns }
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]); // PDOK results when multiple matches
  const [addressError, setAddressError] = useState("");
  const [location, setLocation] = useState(null); // Last successful PDOK match → saved on submit
  const lastAddressQuery = useRef("");
  const addressAbortController = useRef(null);

  // Hooks
  const formId = Location()[3];

  // Bulk invoer is a per-user permission: a logged-in user whose role holds
  // forms.bulkentry keeps the form open after submitting. Logged-out visitors
  // get has() === false → regular single-submit behavior.
  const { has } = usePermissions();
  const bulkEntry = has("forms.bulkentry");

  // Formulierdefinitie — één keer opgehaald via de Cloud Function, want deze
  // route heeft geen login en mag Firestore niet zelf lezen.
  /** @type {ReturnType<typeof useState<object|null>>} */
  const [formData, setFormData] = useState(/** @type {object|null} */ (null));

  const formFields = formData?.fields || [];
  const formTitle = formData?.form?.Title || "";
  const formDescription = formData?.form?.Description || "";

  // -----------------------------
  // Normalize / helpers
  // -----------------------------
  const normalize = (v) => (v || "").trim();
  const norm = (v) => String(v || "").trim().toLowerCase();

  // Matrix cells live in `responses` too, but keyed by (FieldID, RowID, ColumnID).
  // The `!r.RowID` guard keeps them out of the single-value paths — without it a
  // matrix field's first cell would masquerade as the field's own answer.
  const getValue = (fieldId) =>
    responses.find((r) => r.FieldID === fieldId && !r.RowID)?.Value || "";

  const isRequired = (field) => String(field.Required) === "true";

  const isEmptyValue = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string") return v.trim().length === 0;
    return false;
  };

  const validateFieldFormat = (title, value) => {
    const t = (title || "").trim();
    const v = (value || "").trim();
    if (!v) return null; // Empty handled by required check

    if (t === "Telefoonnummer") {
      // Accept: 06xxxxxxxx, +316xxxxxxxx, 0031 6xxxxxxxx, etc.
      // Normalize: strip spaces/dashes, then check
      const digits = v.replace(/[\s\-().]/g, "");
      // Dutch mobile: 06XXXXXXXX (10 digits) or +316XXXXXXXX or 00316XXXXXXXX
      const valid =
        /^06\d{8}$/.test(digits) ||
        /^\+316\d{8}$/.test(digits) ||
        /^00316\d{8}$/.test(digits) ||
        /^\+31[1-9]\d{8}$/.test(digits) ||
        /^0031[1-9]\d{8}$/.test(digits) ||
        /^0[1-9]\d{8}$/.test(digits);
      if (!valid) return "Voer een geldig Nederlands telefoonnummer in (bijv. 06 12345678 of +31 6 12345678).";
    }

    if (t === "E-mail") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
        return "Voer een geldig e-mailadres in (bijv. naam@voorbeeld.nl).";
    }

    if (t === "Postcode") {
      // Dutch postcode: 4 digits + optional space + 2 uppercase letters
      if (!/^\d{4}\s?[A-Za-z]{2}$/.test(v))
        return "Voer een geldige postcode in (bijv. 1234 AB).";
    }

    return null;
  };

  const clearFieldError = (fieldId) =>
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const copy = { ...prev };
      delete copy[fieldId];
      return copy;
    });

  const setValue = (fieldId, value, title) => {
    setResponses((prev) => {
      // `!r.RowID` for the same reason as getValue: matrix cells share the
      // FieldID and must not be mistaken for the field's own single answer.
      const isOwnAnswer = (r) => r.FieldID === fieldId && !r.RowID;

      if (prev.some(isOwnAnswer)) {
        return prev.map((r) =>
          isOwnAnswer(r) ? { ...r, Value: value, Title: title } : r
        );
      }
      return [...prev, { FieldID: fieldId, Value: value, Title: title }];
    });

    clearFieldError(fieldId);
  };

  // -----------------------------
  // Matrix cells
  // -----------------------------
  // Cells live in `responses` alongside ordinary answers, keyed by
  // (FieldID, RowID, ColumnID). Declared here, above validate(), which uses them.
  const getMatrixCells = (fieldId) =>
    responses.filter((r) => r.FieldID === fieldId && r.RowID);

  const getCellValue = (fieldId, rowId, columnId) =>
    responses.find(
      (r) =>
        r.FieldID === fieldId && r.RowID === rowId && r.ColumnID === columnId
    )?.Value || "";

  // An emptied cell is removed outright: an empty cell stores no document.
  const setCellValue = (field, row, column, value) => {
    setResponses((prev) => {
      const matches = (r) =>
        r.FieldID === field.ID &&
        r.RowID === row.ID &&
        r.ColumnID === column.ID;

      if (String(value).trim().length === 0)
        return prev.filter((r) => !matches(r));

      const cell = {
        FieldID: field.ID,
        RowID: row.ID,
        ColumnID: column.ID,
        RowTitle: row.Title || "",
        ColumnTitle: column.Title || "",
        Title: `${row.Title || ""} / ${column.Title || ""}`,
        Value: value,
      };

      return prev.some(matches)
        ? prev.map((r) => (matches(r) ? { ...r, ...cell } : r))
        : [...prev, cell];
    });

    clearFieldError(field.ID);
  };

  // Get response value by response.Title (case-insensitive). Een vraag die aan
  // dit vaste gegeven hangt (FixedFieldKey, T13) gaat voor: die mag anders heten.
  // De sleutels Postcode/Huisnummer/Toevoeging/Straatnaam/Woonplaats zijn gelijk
  // aan de oude titels. AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
  const getResponseValueByTitle = (title) => {
    const bound = (formFields || []).find((f) => f.FixedFieldKey === title);
    const t = norm(title);
    const hit = bound
      ? (responses || []).find((r) => !r.RowID && r.FieldID === bound.ID)
      : (responses || []).find((r) => !r.RowID && norm(r.Title) === t);
    return (hit?.Value || "").trim();
  };

  const fieldByTitle = (title) =>
    (formFields || []).find((f) => norm(f.Title) === norm(title));

  const fillAddressFields = (doc) => {
    const straatnaamField = fieldByTitle("Straatnaam");
    const woonplaatsField = fieldByTitle("Woonplaats");
    const postcodeField = fieldByTitle("Postcode");

    if (straatnaamField && doc.straatnaam) {
      setValue(straatnaamField.ID, doc.straatnaam, straatnaamField.Title);
    }
    if (woonplaatsField && doc.woonplaatsnaam) {
      setValue(woonplaatsField.ID, doc.woonplaatsnaam, woonplaatsField.Title);
    }
    if (postcodeField && doc.postcode) {
      setValue(postcodeField.ID, doc.postcode, postcodeField.Title);
    }

    // Snapshot the rich Location for persistence on submit
    setLocation(buildLocationFromPdokDoc(doc, {
      postcode: getResponseValueByTitle("Postcode"),
      huisnummer: getResponseValueByTitle("Huisnummer"),
      toevoeging: getResponseValueByTitle("Toevoeging"),
    }));
  };

  const selectAddressSuggestion = (doc) => {
    fillAddressFields(doc);
    setAddressSuggestions([]);
  };

  const lookupAddress = async () => {
    const postcode = getResponseValueByTitle("Postcode");
    const huisnummer = getResponseValueByTitle("Huisnummer");
    const toevoeging = getResponseValueByTitle("Toevoeging");
    const straatnaam = getResponseValueByTitle("Straatnaam");

    if (!huisnummer) return;

    const hasPostcode = postcode && /^\d{4}\s?[A-Za-z]{2}$/.test(postcode);
    const hasStraatnaam = straatnaam.length >= 2;

    if (!hasPostcode && !hasStraatnaam) return;

    let query;
    if (hasPostcode) {
      const pc = postcode.replace(/\s/g, "").toUpperCase();
      query = `postcode:${pc} and huisnummer:${huisnummer}`;
    } else {
      query = `straatnaam:${straatnaam} and huisnummer:${huisnummer}`;
    }

    // De toevoeging hoort bij de sleutel: dezelfde PDOK-vraag, maar een andere
    // woning in het pand — die moet opnieuw gekozen worden.
    const lookupKey = `${query}|${toevoeging}`;
    if (lookupKey === lastAddressQuery.current) return;
    lastAddressQuery.current = lookupKey;

    if (addressAbortController.current) {
      addressAbortController.current.abort();
    }
    const controller = new AbortController();
    addressAbortController.current = controller;

    setAddressLoading(true);
    setAddressSuggestions([]);
    setAddressError("");

    const mislukt = () => setLocation(buildLocationFromPdokDoc({}, {
      postcode: getResponseValueByTitle("Postcode"),
      huisnummer: getResponseValueByTitle("Huisnummer"),
      toevoeging: getResponseValueByTitle("Toevoeging"),
      straatnaam: getResponseValueByTitle("Straatnaam"),
      woonplaats: getResponseValueByTitle("Woonplaats"),
      status: 'failed',
    }));

    try {
      if (hasPostcode) {
        // Met een postcode is het antwoord te controleren: klopt postcode én
        // huisnummer, dan invullen; anders zeggen wat er mis is en niets
        // overnemen -- PDOK zoekt fuzzy en geeft anders de buurman terug. In een
        // pand met meerdere woningen kiest de toevoeging; ontbreekt die, dan
        // vraagt de melding erom in plaats van de eerste woning te pakken.
        // AI-CONTEXT: See docs/features/postcode-huisnummer-controle.md
        const controle = await checkAddress({ postcode, huisnummer, toevoeging, signal: controller.signal });
        if (controle.verdict === 'match') {
          fillAddressFields(controle.found);
        } else {
          setAddressError(addressMismatchMessage(controle));
          mislukt();
        }
        return;
      }

      // Zonder postcode: een lijst om uit te kiezen.
      const url = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=${encodeURIComponent(query)}&fl=${PDOK_FL}&rows=10`;
      const res = await fetch(url, { signal: controller.signal });
      const data = await res.json();
      const docs = data?.response?.docs || [];

      if (docs.length === 0) {
        mislukt();
        return;
      }

      if (docs.length === 1) {
        fillAddressFields(docs[0]);
      } else {
        setAddressSuggestions(docs);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("PDOK address lookup failed:", err);
      setAddressError("Adres ophalen mislukt. Vul de velden handmatig in.");
      mislukt();
    } finally {
      setAddressLoading(false);
    }
  };

  const validate = () => {
    const nextErrors = {};

    (formFields || []).forEach((field) => {
      if (field.Type === "matrix") {
        const meta = matrixMeta[field.ID];
        const cells = getMatrixCells(field.ID);

        // Still prefetching. isMatrixComplete treats an unknown grid as "nothing
        // to fill in" and would wave a required matrix straight through.
        if (!meta) {
          if (isRequired(field)) {
            nextErrors[field.ID] = "Even geduld, het formulier laadt nog.";
          }
          return;
        }

        const { rows, columns } = meta;

        if (isRequired(field) && !isMatrixComplete(rows, columns, cells)) {
          nextErrors[field.ID] = "Vul alle velden in.";
          return;
        }

        // A number cell must hold a number even when the field is optional —
        // otherwise the value silently reads as categorical in the analysis
        // layer, which duck-types matrix answers with Number(raw).
        const badCell = cells.some((cell) => {
          const column = (columns || []).find((c) => c.ID === cell.ColumnID);
          return column && !isValidCellValue(cell.Value, getColumnType(column));
        });

        if (badCell) {
          nextErrors[field.ID] = "Vul een geldig getal in bij de getalkolommen.";
        }

        // A matrix has no single Title/Value, so the format rules do not apply.
        return;
      }

      const value = getValue(field.ID);

      if (isRequired(field)) {
        const missing =
          field.Type === "checkbox"
            ? !isCheckboxChecked(value)
            : isEmptyValue(value);
        if (missing) {
          nextErrors[field.ID] = "Dit veld is verplicht.";
          return;
        }
      }

      // A number field must actually hold a number. `<input type="number">`
      // blocks most junk, but a paste or an autofill can still slip through.
      if (field.Type === "number" && !isEmptyValue(value) && Number.isNaN(Number(String(value).trim()))) {
        nextErrors[field.ID] = "Vul een geldig getal in.";
        return;
      }

      const formatError = validateFieldFormat(field.Title, value);
      if (formatError) nextErrors[field.ID] = formatError;
    });

    // Extra validation for standard form: must have a selected target entity
    if (normalize(formType) === "standard") {
      if (!formSelected)
        nextErrors.__formSelected = "Geen deelnemer/adres geselecteerd.";
    }

    if (privacyCheckboxEnabled && !privacyAccepted) {
      nextErrors.__privacy = "Je moet akkoord gaan met het privacybeleid.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // -----------------------------
  // Formulier laden
  // -----------------------------
  // Eén ophaalactie zet alles: de instellingen van het formulier, de velden, de
  // antwoordopties, de matrixdefinities en de privacyteksten. Mislukt hij, dan
  // blijft `formData` null en toont de pagina een foutmelding in plaats van een
  // half formulier waarvan de poorten openstaan.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setFormLoading(true);
      setLoadError("");

      try {
        const payload = await fetchPublicIntakeForm({ client, formId });
        if (cancelled) return;

        setFormData(payload);
        setFormType(payload.form.FormType);
        setFormSelected(payload.form.FormSelected || null);
        setCaptchaEnabled(payload.form.Captcha === true);
        setPrivacyCheckboxEnabled(payload.form.PrivacyCheckbox === true);
        setPrivacyTextBefore(payload.privacy.TextBefore);
        setPrivacyLinkLabel(payload.privacy.LinkLabel);
        setPrivacyLinkUrl(payload.privacy.LinkUrl);
        setPrivacyTextAfter(payload.privacy.TextAfter);
        setThankYouText(payload.thankYouText);
        setMatrixMeta(payload.matrix || {});
      } catch (err) {
        if (cancelled) return;
        setFormData(null);
        setLoadError(
          err?.code === "not-found"
            ? "Dit formulier bestaat niet (meer). Controleer de link of neem contact op met de organisatie."
            : "Het formulier kon niet worden geladen. Ververs de pagina of probeer het later opnieuw."
        );
      } finally {
        if (!cancelled) setFormLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [formId]);

  useEffect(() => {
    return () => {
      if (addressAbortController.current) {
        addressAbortController.current.abort();
      }
    };
  }, []);

  // Matrixdefinities (rijen, kolommen, keuze-opties) komen mee in de payload van
  // het formulier — deze pagina haalt ze niet apart op.

  // -----------------------------
  // Upload helper
  // -----------------------------
  const uploadFile = (file, fieldId) =>
    new Promise((resolve, reject) => {
      const safeName = `${uuid()}-${file.name}`;
      const ref = bucket.ref(`intakeforms/${client}/${formId}/${fieldId}/${safeName}`);
      const task = ref.put(file);
      task.on("state_changed", null, reject, async () => {
        resolve(await task.snapshot.ref.getDownloadURL());
      });
    });

  // -----------------------------
  // Render fields
  // -----------------------------
  // AI-CONTEXT: See docs/patterns.md#ui-intake-form-field-type
  //
  // Elk bedieningselement krijgt `aria-label={field.Title}`. De vraagtekst staat
  // als <h2> BOVEN het veld en niet als <label>, dus zonder dit hoort iemand met
  // een schermlezer alleen 'invoerveld' zonder te weten waarvoor — op een pagina
  // die bewoners zonder inlog invullen. Twee uitzonderingen die het al goed
  // doen: een vinkje zit in een <label> met de vraagtekst erin, en de
  // aankruisvakjes van een meerkeuzevraag krijgen een naam op de GROEP.
  // AI-CONTEXT: See docs/inbox.md #220
  const renderField = (field) => {
    switch (field.Type) {
      case "text":
        return (
          <input
            type="text"
            aria-label={field.Title}
            value={getValue(field.ID)}
            required={isRequired(field)}
            aria-invalid={submitAttempted && !!errors[field.ID]}
            onChange={(e) => setValue(field.ID, e.target.value, field.Title)}
            onBlur={isAddressLookupField(field.Title, field.FixedFieldKey) ? lookupAddress : undefined}
          />
        );

      // Huisnummer is a number field, so this branch has to arm the same
      // address lookup as the text branch — otherwise typing a house number
      // never triggers the PDOK fill of Straatnaam / Woonplaats.
      case "number":
        return (
          <input
            type="number"
            aria-label={field.Title}
            value={getValue(field.ID)}
            required={isRequired(field)}
            aria-invalid={submitAttempted && !!errors[field.ID]}
            onChange={(e) => setValue(field.ID, e.target.value, field.Title)}
            onBlur={isAddressLookupField(field.Title, field.FixedFieldKey) ? lookupAddress : undefined}
          />
        );

      // Een echte datumkiezer, geen tekstveld: anders verzint elke invuller zijn
      // eigen notatie en staat er de ene keer 17-4-1985 en de andere keer
      // 17 april '85. De browser bewaart altijd jjjj-mm-dd.
      case "date":
        return (
          <input
            type="date"
            aria-label={field.Title}
            value={getValue(field.ID)}
            required={isRequired(field)}
            aria-invalid={submitAttempted && !!errors[field.ID]}
            onChange={(e) => setValue(field.ID, e.target.value, field.Title)}
          />
        );

      case "notitie":
        return (
          <textarea
            aria-label={field.Title}
            value={getValue(field.ID)}
            required={isRequired(field)}
            aria-invalid={submitAttempted && !!errors[field.ID]}
            onChange={(e) => setValue(field.ID, e.target.value, field.Title)}
            rows={5}
          />
        );

      case "multiple-one":
        return (
          <IntakeFormSingleChoice
            label={field.Title}
            options={formData?.options?.[field.ID]}
            value={getValue(field.ID)}
            required={isRequired(field)}
            invalid={submitAttempted && !!errors[field.ID]}
            onChange={(waarde) => setValue(field.ID, waarde, field.Title)}
          />
        );

      case "multiple-many":
        return (
          <IntakeFormCheckboxOptions
            label={field.Title}
            options={formData?.options?.[field.ID]}
            selected={getValue(field.ID)}
            onChange={(value) => setValue(field.ID, value, field.Title)}
          />
        );

      case "upload":
        return (
          <input
            type="file"
            aria-label={field.Title}
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) setUploadFiles((prev) => ({ ...prev, [field.ID]: file }));
            }}
          />
        );

      case "checkbox":
        return (
          <label className="checkbox-option-label">
            <input
              type="checkbox"
              checked={isCheckboxChecked(getValue(field.ID))}
              aria-invalid={submitAttempted && !!errors[field.ID]}
              onChange={(e) =>
                setValue(
                  field.ID,
                  e.target.checked ? "true" : "false",
                  field.Title
                )
              }
            />
            <h2>
              {field.Title}{" "}
              {isRequired(field) ? <span aria-hidden="true">*</span> : null}
            </h2>
          </label>
        );

      case "matrix": {
        const { rows, columns, options } = matrixMeta[field.ID] || {};
        return (
          <IntakeFormMatrixField
            field={field}
            rows={rows}
            columns={columns}
            options={options}
            getCellValue={getCellValue}
            setCellValue={setCellValue}
            invalid={submitAttempted && !!errors[field.ID]}
          />
        );
      }

      default:
        return null;
    }
  };

  // -----------------------------
  // Save flow
  // -----------------------------
  // Het aanmaken van het huishouden, het wegschrijven van de antwoorden, de
  // notities en de vastlegging van het privacy-akkoord gebeuren allemaal in de
  // Cloud Function. Die leest het formulier zelf opnieuw uit, dus de vraagtekst
  // en de categorie van een antwoord worden hier niet meegestuurd.
  // AI-CONTEXT: See functions/intakeForms/saveIntakeFormResponse.js

  /**
   * Uploadt de gekozen bestanden en levert ze op als gewone antwoorden.
   * @returns {Promise<Array<{FieldID: string, Value: string}>>}
   */
  const uploadPendingFiles = async () =>
    Promise.all(
      Object.entries(uploadFiles).map(async ([fieldId, file]) => ({
        FieldID: fieldId,
        Value: await uploadFile(file, fieldId),
      }))
    );

  const onSaveSuccess = () => {
    setShowSpinner(true);
    setTimeout(() => {
      setShowSpinner(false);
      setShowResend(true);
    }, 2000);
  };

  const saveForm = async () => {
    if (saving || showSpinner || showResend) return;

    setSubmitAttempted(true);
    setSubmitError("");

    const ok = validate();
    if (!ok) return;

    // Een leeg formulier verzenden deed hiervoor niets, zonder uitleg. De
    // knop lijkt dan kapot.
    if (!responses.length && Object.keys(uploadFiles).length === 0) {
      setSubmitError("Vul minstens één veld in voordat je verzendt.");
      return;
    }

    if (captchaEnabled && !captchaChecked) return;

    if (privacyCheckboxEnabled && !privacyAccepted) return;

    setSaving(true);

    try {
      const uploadResponses = await uploadPendingFiles();

      await submitIntakeForm(
        buildIntakeSubmission({
          client,
          formId,
          responses,
          uploadResponses,
          privacyAccepted,
          location,
          honeypot,
        })
      );

      onSaveSuccess();
    } catch (err) {
      // Zichtbaar falen: hiervoor stopte de spinner gewoon en dacht de bewoner
      // dat het gelukt was, terwijl er niets was opgeslagen.
      console.error("Error saving form:", err);
      setSubmitError(
        err?.message ||
          "Het verzenden is mislukt. Probeer het opnieuw of neem contact op met de organisatie."
      );
    } finally {
      setSaving(false);
    }
  };

  // Zolang het formulier niet geladen is, wordt er niets getoond en niets
  // verstuurd. Een half formulier zou betekenen dat captcha en het
  // privacy-vinkje ontbreken zonder dat iemand dat merkt.
  if (formLoading) {
    return (
      <div className="page-container">
        <div className="event-open-actions">
          <img src={spinner} alt="Formulier laden..." />
        </div>
      </div>
    );
  }

  if (loadError || !formData) {
    return (
      <div className="page-container">
        <div className="event-open-header-container">
          <h1>Formulier niet beschikbaar</h1>
          <p className="field-error" role="alert">
            {loadError || "Het formulier kon niet worden geladen."}
          </p>
        </div>
      </div>
    );
  }

  // Een formulier zonder vragen gaf een witte pagina met alleen 'Verzenden'
  // (testronde 2026-10-28, Fatima B4). Zeg wat er is, en bied niets om te
  // versturen: er valt niets in te vullen.
  if (formFields.length === 0) {
    return (
      <div className="page-container">
        <div className="event-open-header-container">
          {formTitle && <h1>{formTitle}</h1>}
          <p>Dit formulier heeft nog geen vragen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="event-open-header-container">
        <div>
          <h1>{formTitle}</h1>
          <p>{formDescription}</p>

          {submitAttempted && errors.__formSelected ? (
            <p className="field-error" role="alert">
              {errors.__formSelected}
            </p>
          ) : null}
        </div>
      </div>

      <div className="event-open-fields-container">
        {formFields &&
          formFields.map((field) => (
            <div key={field.ID} className="event-open-field">
              {field.Type !== "checkbox" ? (
                <h2>
                  {field.Title}{" "}
                  {String(field.Required) === "true" ? (
                    <span aria-hidden="true">*</span>
                  ) : null}
                </h2>
              ) : null}

              <p>{field.Description}</p>

              {renderField(field)}

              {addressLoading &&
                (norm(field.Title) === "straatnaam" ||
                  norm(field.Title) === "woonplaats") ? (
                <p className="field-loading">Adres ophalen...</p>
              ) : null}

              {addressSuggestions.length > 0 &&
                norm(field.Title) === "straatnaam" ? (
                <div className="address-suggestions">
                  <p>Meerdere adressen gevonden. Selecteer het juiste adres:</p>
                  <ul className="address-suggestions-list">
                    {addressSuggestions.map((doc) => (
                      <li
                        key={doc.id}
                        className="address-suggestion-item"
                        onClick={() => selectAddressSuggestion(doc)}
                      >
                        {doc.weergavenaam}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* Onder 'Straatnaam', en als dat veld er niet is onder 'Huisnummer':
                  anders staat de melding nergens. */}
              {addressError && norm(field.Title) === (fieldByTitle("Straatnaam") ? "straatnaam" : "huisnummer") ? (
                <p className="field-error">{addressError}</p>
              ) : null}

              {submitAttempted && errors[field.ID] ? (
                <p className="field-error" role="alert">
                  {errors[field.ID]}
                </p>
              ) : null}
            </div>
          ))}
      </div>

      <div className="event-open-actions">
        {showSpinner ? (
          <img src={spinner} alt="Bezig met opslaan..." />
        ) : (
          <>
            <input
              className="intake-honeypot"
              type="text"
              name={HONEYPOT_KEY}
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            {captchaEnabled && !showResend && (
              <label className="captcha-label">
                <input
                  type="checkbox"
                  checked={captchaChecked}
                  onChange={(e) => setCaptchaChecked(e.target.checked)}
                />
                Ik ben geen robot
              </label>
            )}
            {privacyCheckboxEnabled && !showResend && (
              <>
                <div className="captcha-label">
                  <input
                    type="checkbox"
                    id="privacy-accept"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  />
                  <span>
                    {privacyTextBefore ? (
                      <label htmlFor="privacy-accept">
                        {privacyTextBefore}{" "}
                      </label>
                    ) : null}
                    {privacyLinkLabel && /^(https?:\/\/|mailto:)/i.test(privacyLinkUrl) ? (
                      <a
                        href={privacyLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {privacyLinkLabel}
                      </a>
                    ) : null}
                    {privacyTextAfter ? (
                      <label htmlFor="privacy-accept">
                        {privacyTextAfter}
                      </label>
                    ) : null}
                  </span>
                </div>
                {submitAttempted && errors.__privacy ? (
                  <p className="field-error" role="alert">
                    {errors.__privacy}
                  </p>
                ) : null}
              </>
            )}
            {submitError ? (
              <p className="field-error" role="alert">
                {submitError}
              </p>
            ) : null}
            <button
              className="button"
              onClick={(showResend && bulkEntry) ? () => { setShowResend(false); setSaving(false); } : saveForm}
              disabled={
                saving ||
                (showResend && !bulkEntry) ||
                (captchaEnabled && !captchaChecked && !showResend) ||
                (privacyCheckboxEnabled && !privacyAccepted && !showResend)
              }
            >
              {saving ? "Bezig met opslaan..." : (showResend && bulkEntry) ? "Opnieuw versturen" : "Verzenden"}
            </button>
          </>
        )}
      </div>

      {showResend && !bulkEntry ? (
        <p className="intakeform-thankyou-text">
          {thankYouText || "Bedankt voor het invullen. Je kunt het formulier nu sluiten."}
        </p>
      ) : null}
    </div>
  );
};

export default IntakeFormOpen;